import { useEffect, useState } from 'react';

import { BrandProductPreviewResource } from '@/entities/Brand/api';
import { ConsoleDev } from '@/Lib/ConsoleDev';

interface UseBrandActionConfirmControlParameters {
    brandId: number;

    actionOnlyBrand: () => Promise<void>;
    actionBrandWithProducts: () => Promise<void>;

    getPreviewProducts: (brandId: number) => Promise<BrandProductPreviewResource[]>;

    close: () => void;
}

export const useBrandActionConfirmControl = (parameters: UseBrandActionConfirmControlParameters) => {
    const [previewProductsBrand, setPreviewProductsBrand] = useState<BrandProductPreviewResource[]>([]);

    const [isLoading, setIsLoading] = useState<boolean>(false);

    const withLoading = (fn: () => Promise<void>) => {
        return async () => {
            if (isLoading) {
                return;
            }

            try {
                setIsLoading(true);

                await fn();
            } catch (error) {
                ConsoleDev.error(error);
            } finally {
                setIsLoading(false);
            }
        };
    };

    const actionOnlyBrand = withLoading(async () => {
        await parameters.actionOnlyBrand();

        parameters.close();
    });

    const actionBrandWithProducts = withLoading(async () => {
        await parameters.actionBrandWithProducts();

        parameters.close();
    });

    useEffect(() => {
        const loadPreviewBrands = async () => {
            const previewProducts = await parameters.getPreviewProducts(parameters.brandId);

            setPreviewProductsBrand(previewProducts);
        };

        if (previewProductsBrand.length === 0) {
            loadPreviewBrands();
        }
    }, [parameters.brandId, parameters.getPreviewProducts, previewProductsBrand.length]);

    return {
        isLoading,
        previewProductsBrand,
        actionOnlyBrand,
        actionBrandWithProducts,
    };
};
